namespace $.$$ {

	type $shm_projects_goal_chat_item = $shm_codex_api_item & {
		id: string
		type: 'userMessage' | 'agentMessage'
		role: 'user' | 'agent' | 'system'
		time: number
		reasoning: readonly $shm_projects_goal_thinking_entry[]
	}

	export class $shm_projects_goal_chat extends $.$shm_projects_goal_chat {

		message_rows() {
			const messages = this.message_items().map( item => this.Message( item.id ) )
			return [
				... messages.slice( 0, 1 ),
				... this.clarifications(),
				... messages.slice( 1 ),
				... this.after(),
			]
		}

		Message_thinking( id: string ) {
			return this.project_message( id )?.reasoning.length ? super.Message_thinking( id ) : null as any
		}

		message_thinking_title( id: string ) {
			return `Размышления (${ this.project_message( id )?.reasoning.length ?? 0 })`
		}

		reasoning_arg( id: string ) {
			return { project_thinking: id }
		}

		@ $mol_action
		override reasoning_select( id: string, next?: MouseEvent ) {
			if( next === undefined ) return null
			next.preventDefault()
			this.thinking_id( id )
			return next
		}

		@ $mol_mem
		thinking_id( next?: string ) {
			return this.$.$mol_state_arg.value(
				'project_thinking',
				next === undefined ? undefined : next || null,
			) ?? ''
		}

		thinking_entries(): $shm_projects_goal_thinking_entry[] {
			const item = this.project_message( this.thinking_id() )
			return [ ...( item?.reasoning ?? [] ) ]
		}

		thinking_visible() {
			return Boolean( this.thinking_entries().length )
		}

		@ $mol_action
		override thinking_close( next?: Event ) {
			if( next === undefined ) return null
			this.thinking_id( '' )
			return next
		}

		message_items(): $shm_projects_goal_chat_item[] {
			const run = this.run() as $shm_projects_goal_run | null
			if( !run ) return []
			return this.prerelease()
				? this.prerelease_messages( run )
				: this.iteration_messages( run, this.iteration() || run.iteration )
		}

		iteration_messages( run: $shm_projects_goal_run, iteration: number ) {
			const result = ( run.result_history ?? [] ).find( item => item.iteration === iteration )
			const started = result?.started_at ?? ( iteration === run.iteration ? run.iteration_started_at : 0 ) ?? run.started_at
			const goal = iteration === 1 ? run.goal : run.refinements[ iteration - 2 ] || run.goal
			const items: $shm_projects_goal_chat_item[] = [ this.chat_item(
				`iteration:${ iteration }:user`,
				'user',
				goal,
				started || run.started_at,
			) ]
			let reasoning = [] as $shm_projects_goal_thinking_entry[]
			for( const [ index, event ] of run.events.entries() ) {
				if( event.iteration !== iteration || event.scope === 'prerelease' || !event.text.trim() ) continue
				if( event.kind === 'reasoning' ) {
					reasoning.push( this.reasoning_entry( event.id || `iteration:${ iteration }:reasoning:${ index }`, event ) )
					continue
				}
				const attached = event.kind === 'system' ? [] : this.reasoning_finished( reasoning )
				items.push( this.chat_item(
					event.id || `iteration:${ iteration }:event:${ index }`,
					event.kind === 'system' ? 'system' : 'agent',
					this.event_text_without_images( event.text ),
					event.time,
					attached,
				) )
				if( attached.length ) reasoning = []
			}

			if( result ) items.push( this.chat_item(
				`iteration:${ iteration }:result`,
				'agent',
				this.result_text( result ),
				result.completed_at,
				this.reasoning_finished( reasoning ),
			) )
			else if( reasoning.length ) items.push( this.chat_item(
				`iteration:${ iteration }:progress`,
				'agent',
				'',
				reasoning.at( -1 )?.time ?? run.updated_at,
				reasoning,
			) )
			return items
		}

		prerelease_messages( run: $shm_projects_goal_run ) {
			const prerelease = run.prerelease
			if( !prerelease ) return []
			const items: $shm_projects_goal_chat_item[] = [ this.chat_item(
				'prerelease:user',
				'user',
				`Подготовить пререлиз версии v${ this.version() }`,
				prerelease.started_at,
			) ]
			let reasoning = [] as $shm_projects_goal_thinking_entry[]
			for( const [ index, event ] of prerelease.events.entries() ) {
				if( !event.text.trim() ) continue
				if( event.kind === 'reasoning' ) {
					reasoning.push( this.reasoning_entry( event.id || `prerelease:reasoning:${ index }`, event ) )
					continue
				}
				const attached = event.kind === 'system' ? [] : this.reasoning_finished( reasoning )
				items.push( this.chat_item(
					event.id || `prerelease:event:${ index }`,
					event.kind === 'system' ? 'system' : 'agent',
					this.event_text_without_images( event.text ),
					event.time,
					attached,
				) )
				if( attached.length ) reasoning = []
			}
			if( prerelease.status === 'done' || prerelease.status === 'failed' || prerelease.status === 'stopped' ) {
				const generic = prerelease.results.length === 1
					&& prerelease.results[0].title === 'Пререлиз'
					&& prerelease.results[0].description === 'Пререлиз'
				const text = prerelease.results.length && !generic
					? this.changes_text( prerelease.results )
					: prerelease.summary || prerelease.error || ( prerelease.status === 'stopped' ? 'Работа остановлена.' : 'Пререлиз не завершён.' )
				items.push( this.chat_item(
					'prerelease:result',
					'agent',
					text,
					prerelease.completed_at ?? run.updated_at,
					this.reasoning_finished( reasoning ),
				) )
			} else if( reasoning.length ) items.push( this.chat_item(
				'prerelease:progress',
				'agent',
				'',
				reasoning.at( -1 )?.time ?? run.updated_at,
				reasoning,
			) )
			return items
		}

		chat_item(
			id: string,
			role: $shm_projects_goal_chat_item['role'],
			text: string,
			time: number,
			reasoning: readonly $shm_projects_goal_thinking_entry[] = [],
		) {
			return {
				id,
				type: role === 'user' ? 'userMessage' as const : 'agentMessage' as const,
				role,
				text,
				time,
				reasoning,
			}
		}

		reasoning_entry(
			id: string,
			event: $shm_projects_goal_run['events'][number],
		): $shm_projects_goal_thinking_entry {
			return {
				id,
				title: event.phase || 'Подготовка',
				text: this.event_text_without_images( event.text ),
				time: event.time,
				finished: false,
			}
		}

		reasoning_finished( entries: readonly $shm_projects_goal_thinking_entry[] ) {
			return entries.map( entry => ({ ... entry, finished: true }) )
		}

		result_text( result: NonNullable< $shm_projects_goal_run['result_history'] >[number] ) {
			if( result.changes.length ) return this.changes_text( result.changes )
			return result.summary || 'Работа завершена.'
		}

		changes_text( changes: readonly { title: string; description: string; screenshots: readonly string[] }[] ) {
			return [
				'**Что изменилось**',
				... changes.flatMap( change => [
					`**${ change.title }**`,
					change.description,
					... change.screenshots.map( ( path, index )=> `![${ change.title } — ${ index + 1 }](${ this.resource_uri( path ) })` ),
				].filter( Boolean ) ),
			].join( '\n\n' )
		}

		resource_uri( path: string ) {
			return this.$.$shm_projects_config.resource_url( path, true, this.workspace_id() )
		}

		event_text_without_images( text: string ) {
			return text.replace( /!\[[^\]]*\]\([^\n)]*\)\s*/g, '' ).trim()
		}

		override message_role( id: string ) {
			switch( this.project_message( id )?.role ) {
				case 'user': return 'Вы'
				case 'system': return 'Статус'
				default: return 'Агент'
			}
		}

		override message_role_key( id: string ) {
			return this.project_message( id )?.role ?? ''
		}

		override message_theme( id: string ) {
			return this.project_message( id )?.role === 'agent' ? '$mol_theme_special' : ''
		}

		override message_time( id: string ) {
			const time = this.project_message( id )?.time
			return time ? new Date( time ).toLocaleString() : ''
		}

		override message_text( id: string ) {
			return this.project_message( id )?.text ?? ''
		}

		project_message( id: string ) {
			return this.message_items().find( item => item.id === id )
		}

	}

}
